import React from 'react';
import { Loader2 } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
    variant?: 'primary' | 'secondary' | 'danger' | 'ghost' | 'outline';
    size?: 'sm' | 'md' | 'lg';
    isLoading?: boolean;
    leftIcon?: React.ReactNode;
    rightIcon?: React.ReactNode;
}

export const Button: React.FC<ButtonProps> = ({
    children,
    variant = 'primary',
    size = 'md',
    isLoading = false,
    leftIcon,
    rightIcon,
    className = '',
    disabled,
    ...props
}) => {
    const { theme } = useTheme();

    const baseStyles = 'relative overflow-hidden inline-flex items-center justify-center rounded-lg font-medium transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed active:scale-[0.98]';

    const sizeStyles = {
        sm: 'px-3 py-1.5 text-xs',
        md: 'px-4 py-2 text-sm',
        lg: 'px-6 py-3 text-base',
    };

    const variantStyles = {
        primary: theme === 'dark'
            ? 'bg-storgia-blurple text-white hover:bg-storgia-blurple/90 focus:ring-storgia-blurple shadow-[0_0_15px_rgba(88,101,242,0.3)] hover:shadow-[0_0_25px_rgba(88,101,242,0.5)] border border-transparent'
            : 'bg-indigo-600 text-white hover:bg-indigo-700 focus:ring-indigo-500 shadow-sm hover:shadow-md',
        secondary: theme === 'dark'
            ? 'bg-storgia-light-blurple/10 text-storgia-light-blurple hover:bg-storgia-light-blurple/20 focus:ring-storgia-light-blurple border border-storgia-light-blurple/20'
            : 'bg-indigo-50 text-indigo-700 hover:bg-indigo-100 focus:ring-indigo-500 border border-indigo-100',
        danger: theme === 'dark'
            ? 'bg-red-900/20 text-red-400 hover:bg-red-900/30 focus:ring-red-500 border border-red-500/20'
            : 'bg-red-50 text-red-700 hover:bg-red-100 focus:ring-red-500 border border-red-100',
        ghost: theme === 'dark'
            ? 'text-gray-400 hover:text-white hover:bg-white/5 focus:ring-gray-500'
            : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100 focus:ring-gray-500',
        outline: theme === 'dark'
            ? 'border border-storgia-light-blurple/30 text-storgia-light-blurple hover:bg-storgia-light-blurple/10 focus:ring-storgia-blurple'
            : 'border border-gray-300 text-gray-700 hover:bg-gray-50 focus:ring-indigo-500',
    };

    return (
        <button
            className={`${baseStyles} ${sizeStyles[size]} ${variantStyles[variant]} ${className}`}
            disabled={disabled || isLoading}
            {...props}
        >
            {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {!isLoading && leftIcon && <span className="mr-2">{leftIcon}</span>}
            {children}
            {!isLoading && rightIcon && <span className="ml-2">{rightIcon}</span>}
        </button>
    );
};
